import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './P2pTradeChat.css';

interface ChatMessage {
  id: string;
  text: string;
  time: string;
  sender: 'trader' | 'buyer';
}

const mockChatData = {
  orderId: '9226448442254659',
  trader: {
    name: 'Daniel Abraham',
    initial: 'D',
    verified: true,
    status: 'Usually replies instantly'
  },
  messages: [
    {
      id: '1',
      text: "Hello! I'm ready to complete this trade. Please make the payment to the account details provided.",
      time: '2:30 PM',
      sender: 'trader' as const
    },
    {
      id: '2',
      text: "I've just transferred the payment. It should reflect in your account shortly.",
      time: '2:31 PM',
      sender: 'buyer' as const
    },
    {
      id: '3',
      text: "Thank you! Let me verify the payment details. Can you please share your Order ID?",
      time: '2:32 PM',
      sender: 'trader' as const
    },
    {
      id: '4',
      text: 'Order ID: 9226448442254659',
      time: '2:33 PM',
      sender: 'buyer' as const
    },
    {
      id: '5',
      text: "Thank you. I'm reviewing your order now. Please hold on for a moment while I verify the payment details.",
      time: '2:34 PM',
      sender: 'trader' as const
    }
  ]
};

const P2pTradeChat: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>(mockChatData.messages);
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSendMessage = () => {
    if (messageInput.trim() === '') return;

    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      text: messageInput,
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      sender: 'buyer'
    };

    setMessages([...messages, newMessage]);
    setMessageInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAttachment = () => {
    console.log('Open attachment picker...');
  };

  return (
    <div className="p2p-chat-container">
      <header className="p2p-chat-header">
        <button className="p2p-chat-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="p2p-chat-avatar">
          {mockChatData.trader.initial}
        </div>

        <div className="p2p-chat-header-info">
          <div className="p2p-chat-header-title-row">
            <h1 className="p2p-chat-title">{mockChatData.trader.name}</h1>
            {mockChatData.trader.verified && (
              <div className="p2p-chat-verified-badge">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M6 0L7 4L11 5L7 6L6 10L5 6L1 5L5 4L6 0Z" fill="currentColor"/>
                </svg>
              </div>
            )}
          </div>
          <div className="p2p-chat-status">{mockChatData.trader.status}</div>
        </div>
      </header>

      <div className="p2p-chat-messages-container">
        <div className="p2p-chat-messages">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`p2p-chat-message-wrapper ${message.sender === 'buyer' ? 'p2p-chat-message-buyer' : 'p2p-chat-message-trader'}`}
            >
              {message.sender === 'trader' && (
                <div className="p2p-chat-message-avatar">
                  {mockChatData.trader.initial}
                </div>
              )}
              <div className="p2p-chat-message-content">
                <div className={`p2p-chat-message-bubble ${message.sender === 'buyer' ? 'p2p-chat-bubble-buyer' : 'p2p-chat-bubble-trader'}`}>
                  {message.text}
                </div>
                <div className="p2p-chat-message-time">{message.time}</div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="p2p-chat-input-container">
        <div className="p2p-chat-input-wrapper">
          <div className="p2p-chat-input-box">
            <textarea
              className="p2p-chat-textarea"
              placeholder="Type your message..."
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
            />
            <button 
              className="p2p-chat-attach-button" 
              onClick={handleAttachment}
              aria-label="Attach file"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 7.5L7.5 14C6.12 15.38 3.88 15.38 2.5 14C1.12 12.62 1.12 10.38 2.5 9L9 2.5C9.95 1.55 11.55 1.55 12.5 2.5C13.45 3.45 13.45 5.05 12.5 6L6.5 12C6.23 12.27 5.77 12.27 5.5 12C5.23 11.73 5.23 11.27 5.5 11L11 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <button 
            className={`p2p-chat-send-button ${messageInput.trim() === '' ? 'p2p-chat-send-disabled' : ''}`}
            onClick={handleSendMessage}
            disabled={messageInput.trim() === ''}
            aria-label="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M14.5 1.5L7 9M14.5 1.5L9.5 14.5L7 9M14.5 1.5L1.5 6.5L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default P2pTradeChat;